import * as React from "react";
import { Box, Button, Chip, Stack, Tooltip, Typography } from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import { ColumnDef, DataTable } from "../../components/Table";
import type { Kpi } from "../../types";
import { COLORS } from "../../theme/theme-options";
import DialogContext from "../../contexts/DialogContext";
import { useKpis } from "../../hooks/query/useKpis";
import { DeleteButton } from "../../components/buttons/DeleteButton";
import RowActionsMenu from "../../components/RowActionsMenu";

const DESCRIPTION_MAX_CHARS = 30;

const truncateDescription = (value: string): { text: string; isTruncated: boolean } => {
  if (value.length <= DESCRIPTION_MAX_CHARS) {
    return {
      text: value,
      isTruncated: false,
    };
  }

  return {
    text: `${value.slice(0, DESCRIPTION_MAX_CHARS).trimEnd()}...`,
    isTruncated: true,
  };
};

const columns: ColumnDef<Kpi>[] = [
  {
    id: "name",
    header: "Name",
    render: (row) => {
      return row.name;
    },
  },
  {
    id: "description",
    header: "Description",
    render: (row) => {
      const description = row.description ?? "";
      const result = truncateDescription(description);

      if (result.isTruncated === false) {
        return result.text;
      }

      return (
        <Tooltip title={description} arrow>
          <Typography component="span" sx={{ cursor: "help" }}>
            {result.text}
          </Typography>
        </Tooltip>
      );
    },
  },
  {
    id: "source",
    header: "Source",
    render: (row) => {
      return row.system ? (
        <Chip label="Default" size="small" color="info" />
      ) : (
        <Chip label="Custom" size="small" />
      );
    },
  },
  {
    id: "warning",
    header: "Warning",
    align: "right",
    headerAlign: "right",
    render: (row) => {
      return row.thresholds.warning;
    },
  },
  {
    id: "critical",
    header: "Critical",
    align: "right",
    headerAlign: "right",
    render: (row) => {
      return row.thresholds.critical;
    },
  },
];

const KpisConfigPage: React.FC = () => {
  const { data: kpis = [], isLoading } = useKpis();
  const { show } = React.useContext(DialogContext);
  const [selectedIds, setSelectedIds] = React.useState<string[]>([]);
  const [rowMenu, setRowMenu] = React.useState<{
    anchorEl: HTMLElement;
    kpi: Kpi;
  } | null>(null);

  const deletableSelectedCount = React.useMemo(() => {
    return selectedIds.filter((id) => {
      const kpi = kpis.find((row) => row.id === id);
      return kpi ? kpi.system !== true : false;
    }).length;
  }, [kpis, selectedIds]);

  return (
    <Box display="flex" flexDirection="column" gap={2.2}>
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={1.5}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
      >
        <Stack spacing={0.4}>
          <Typography sx={{ fontSize: 18, fontWeight: 800, color: COLORS.grey[100] }}>
            KPIs
          </Typography>
          <Typography sx={{ color: COLORS.grey[500] }}>
            Default KPI catalog from constants plus your custom KPIs
          </Typography>
        </Stack>
        <Stack direction="row" spacing={1}>
          <DeleteButton
            disabled={deletableSelectedCount === 0}
            disabledReason={
              selectedIds.length === 0
                ? "Select at least one KPI."
                : "System KPIs cannot be deleted."
            }
            onClick={() => {
              if (deletableSelectedCount === 0) {
                return;
              }

              show({
                dialog: "confirm-delete",
                entity: "kpi",
                ids: selectedIds.join(","),
              });
            }}
          />
          <Button
            variant="outlined"
            startIcon={<AddRoundedIcon fontSize="small" />}
            onClick={() => {
              show({ dialog: "new-kpi" });
            }}
          >
            New
          </Button>
        </Stack>
      </Stack>

      <DataTable
        title="KPI Catalog"
        loading={isLoading}
        rows={kpis}
        columns={columns}
        colSpan={7}
        onSelectionChange={setSelectedIds}
        onRowMenuClick={(kpi, anchorEl) => {
          setRowMenu({
            kpi,
            anchorEl,
          });
        }}
      />

      <RowActionsMenu
        anchorEl={rowMenu?.anchorEl ?? null}
        open={Boolean(rowMenu)}
        onClose={() => setRowMenu(null)}
        disableEdit={rowMenu ? rowMenu.kpi.system === true : true}
        disableDelete={rowMenu ? rowMenu.kpi.system === true : true}
        disableEditReason="System KPIs are read-only."
        disableDeleteReason="System KPIs cannot be deleted."
        onEdit={() => {
          const kpiId = rowMenu?.kpi.id;
          if (!kpiId) {
            return;
          }
          show({ dialog: "edit-kpi", kpiId });
        }}
        onDelete={() => {
          const kpiId = rowMenu?.kpi.id;
          if (!kpiId) {
            return;
          }
          show({
            dialog: "confirm-delete",
            entity: "kpi",
            ids: kpiId,
          });
        }}
      />
    </Box>
  );
};

export default KpisConfigPage;
